/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { FormControl, FormGroup } from 'react-bootstrap'
import { useForm, Controller, useFormState, useWatch } from 'react-hook-form'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { PresenterProps } from 'plugin-aeapps-resources-manager/shared/api/IInfoPresenter'

import { NoInfo } from '../../AssetsManager/components/ViewAssets/style'
import { hasAdminPermission } from '../../utils/permissions'
import { getAssetTypes, getAffiliatedList } from '../../utils/services'
import { AssetType, getAssetType, getAssetTypesToResourceGroup } from '../../utils/services/assetType'
import { getAsset, IGeneralAsset } from '../../utils/services/generalAsset'
import { Output, OutputEdit, Label, SelectEdit } from './style'

export interface IBasicInfo {
    avatar: string
    name: string
    assetCode: string
    description?: string
    responsiblePerson?: string
    affiliatedTo?: number | IGeneralAsset
    affiliatedToName?: string
    assetType: string | AssetType
    id: number
    generalAssetId: number
}

type ISelect = {
    label: string
    value: string
}

interface IFormData {
    avatar: string
    name: string
    assetCode: string
    description?: string
    responsiblePerson?: string
    affiliatedTo?: ISelect
    assetType: ISelect
}

export const BasicInfoComponent: React.FC<PresenterProps<IBasicInfo>> = ({
    editMode,
    info,
    onChanged,
    requestError
}) => {
    const formDefault: IFormData = {
        avatar: '',
        name: '',
        assetCode: '',
        description: '',
        responsiblePerson: '',
        affiliatedTo: { label: null, value: null },
        assetType: { label: null, value: null }
    }

    const { control, setValue } = useForm<IFormData>({
        mode: 'all',
        defaultValues: formDefault
    })

    const { isValid } = useFormState({ control })
    const watch = useWatch({ control })

    const [listAffiliated, setListAffiliated] = useState<ISelect[]>([])
    const [assetTypeSelectList, setAssetTypeSelectList] = useState<ISelect[]>([])
    const [internalInfo, setInternalInfo] = useState<IBasicInfo>()
    const [fieldError, setFieldError] = useState<string>()
    const [selectedField, setSelectedField] = useState<string>()

    const handleGetAssetTypes = async (): Promise<ISelect[]> => {
        try {
            const data = hasAdminPermission ? await getAssetTypesToResourceGroup() : await getAssetTypes()
            const newAssetTypes = data.map(el => {
                return { label: el.name, value: el.id }
            })

            return newAssetTypes
        } catch (error) {
            console.log(error)
        }
    }

    const handleGetAffiliated = async (): Promise<ISelect[]> => {
        try {
            return (await getAffiliatedList()).map(el => {
                return { label: el ? el.affiliatedToName : 'Unnamed', value: el.affiliatedToId.toString() }
            })
        } catch (error) {
            console.log(error)
        }
    }

    const init = async (): Promise<void> => {
        const newInfo = { ...info }

        newInfo.assetType = await getAssetType(info.assetType as string)
        if (info.affiliatedTo) {
            newInfo.affiliatedTo = await getAsset(info.affiliatedTo as number)
        }

        setInternalInfo(newInfo)
    }

    const onEnterEditMode = async (): Promise<void> => {
        if (internalInfo) {
            setValue('avatar', info.avatar)
            internalInfo.name && setValue('name', info.name)
            internalInfo.assetCode && setValue('assetCode', info.assetCode)
            internalInfo.description && setValue('description', info.description)
            internalInfo.responsiblePerson && setValue('responsiblePerson', info.responsiblePerson)
            const assetType = internalInfo.assetType as AssetType
            internalInfo.assetType && setValue('assetType', { label: assetType.name, value: assetType.id })
            const affiliatedTo = internalInfo.affiliatedTo as IGeneralAsset
            internalInfo.affiliatedTo
                ? setValue('affiliatedTo', {
                      label: affiliatedTo.basicInfo?.name,
                      value: affiliatedTo.id.toString()
                  })
                : setValue('affiliatedTo', undefined)
        }

        const assetTypes = await handleGetAssetTypes()
        const affiliatedList = await handleGetAffiliated()
        setListAffiliated(affiliatedList)
        setAssetTypeSelectList(assetTypes)
    }

    useEffect(() => {
        if (requestError === undefined || requestError === null) {
            return
        }

        if (requestError === 'Duplicated Asset Code.') {
            globalNotification.error('Duplicated Resource Code')
            setFieldError('assetCode')
        }
    }, [requestError])

    useEffect(() => {
        if (editMode) {
            // init()
            onEnterEditMode()
        }
    }, [editMode, info])

    useEffect(() => {
        init()
    }, [editMode])

    const validationEdit = (): boolean => {
        if (
            info.affiliatedTo === watch.affiliatedTo?.value &&
            info.assetCode === watch.assetCode &&
            info.assetType === watch.assetType.value &&
            info.description === watch.description &&
            info.name === watch.name &&
            info.responsiblePerson === watch.responsiblePerson
        ) {
            return false
        } else {
            return true
        }
    }

    useEffect(() => {
        const formatValues: IBasicInfo = {
            avatar: watch.avatar,
            responsiblePerson: watch.responsiblePerson,
            assetType: watch?.assetType?.value,
            generalAssetId: info.generalAssetId,
            description: watch.description,
            assetCode: watch.assetCode,
            name: watch.name,
            affiliatedTo: watch.affiliatedTo?.value ? Number(watch.affiliatedTo.value) : null,
            id: info.id
        }
        onChanged({ validated: isValid && validationEdit(), infoNewState: formatValues })
    }, [watch, isValid])

    const editModeRender = (): JSX.Element => {
        return (
            <>
                <OutputEdit>
                    <Controller
                        rules={{ required: false }}
                        name={'avatar'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group avatar-text-field"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <FormControl
                                        type="text"
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        componentClass={'textarea'}
                                        className="avatarField"
                                        id="resource-avatar-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>

                <OutputEdit sizeField="50%" withRightBorder>
                    <Controller
                        rules={{ required: true, validate: value => value.trim().length > 0 }}
                        name={'name'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <Label>{i18n('Name')}</Label>
                                    <FormControl
                                        className="input"
                                        type="text"
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        autoFocus
                                        disabled={!editMode}
                                        maxLength={50}
                                        id="resource-name-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>

                <OutputEdit sizeField="25%" withRightBorder>
                    <Controller
                        rules={{
                            required: true,
                            validate: value => value.trim().length > 0
                        }}
                        name={'assetCode'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <Label>{i18n('Resource Code')}</Label>
                                    <FormControl
                                        type="text"
                                        className="input"
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        maxLength={8}
                                        id="resource-code-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>

                <OutputEdit sizeField="25%">
                    <Controller
                        rules={{ required: true, validate: value => value.trim().length > 0 }}
                        name={'responsiblePerson'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <Label>{i18n('Responsible')}</Label>
                                    <FormControl
                                        className="input"
                                        type="text"
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        maxLength={24}
                                        id="resource-responsible-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>

                <OutputEdit sizeField="100%">
                    <Controller
                        rules={{ required: false }}
                        name={'description'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <Label>{i18n('Description')}</Label>
                                    <FormControl
                                        componentClass="textarea"
                                        type="text"
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        maxLength={255}
                                        id="resource-description-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>

                <OutputEdit sizeField="50%" withRightBorder>
                    <Controller
                        rules={{
                            required: true
                        }}
                        name={'assetType'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <Label>{i18n('Resource Type')}</Label>
                                    <SelectEdit
                                        options={assetTypeSelectList}
                                        isSearchable={true}
                                        onChange={(value, obj) => {
                                            field.onChange(obj)
                                        }}
                                        value={field.value}
                                        searchable={false}
                                        onBlur={field.onBlur}
                                        clearable={false}
                                        id="resource-type-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>

                <OutputEdit sizeField="50%">
                    <Controller
                        rules={{ required: false }}
                        name={'affiliatedTo'}
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <FormGroup
                                    className="input-form-group"
                                    validationState={fieldState.error ? 'error' : 'success'}
                                >
                                    <Label>{i18n('Affiliated to')}</Label>
                                    <SelectEdit
                                        options={listAffiliated}
                                        isSearchable={true}
                                        onChange={(value, obj) => {
                                            field.onChange(obj)
                                        }}
                                        value={field.value ? field.value : undefined}
                                        searchable={false}
                                        onBlur={field.onBlur}
                                        id="resource-affiliated-edition"
                                    />
                                </FormGroup>
                            )
                        }}
                    />
                </OutputEdit>
            </>
        )
    }

    const viewModeRender = (): JSX.Element => {
        return internalInfo ? (
            <>
                <Output sizeField="50%" withRightBorder>
                    <Label>{i18n('Name')}</Label>
                    <span className="contentLabel">{internalInfo.name}</span>
                </Output>
                <Output sizeField="25%" withRightBorder>
                    <Label>{i18n('Resource Code')}</Label>
                    <span className="contentLabel">{internalInfo.assetCode}</span>
                </Output>
                <Output sizeField="25%">
                    <Label>{i18n('Responsible')}</Label>
                    <span className="contentLabel affiliated text-overflow">{internalInfo.responsiblePerson}</span>
                </Output>
                <Output sizeField="100%">
                    <Label>{i18n('Description')}</Label>
                    <span className="contentLabel">{internalInfo.description}</span>
                </Output>
                <Output sizeField="50%" withRightBorder>
                    <Label>{i18n('Resource Type')}</Label>
                    <span className="contentLabel">{(internalInfo.assetType as AssetType)?.name}</span>
                </Output>
                <Output sizeField="50%">
                    <Label>{i18n('Affiliated to')}</Label>
                    <span className="contentLabel affiliated">
                        {(internalInfo.affiliatedTo as IGeneralAsset)?.basicInfo?.name}
                    </span>
                </Output>
            </>
        ) : (
            <NoInfo>
                <i className="fa fa-spinner fa-spin" />
            </NoInfo>
        )
    }

    return editMode ? editModeRender() : viewModeRender()
}
